import { PageBackLink } from "@/components/page-back-link"
import { LabDetailSkeleton } from "@/features/labs/components/lab-detail-skeleton"
import { Button } from "@/components/ui/button"
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Activity, Clock, ExternalLink, FileText, ShoppingBag } from 'lucide-react'
import { ErrorBanner, ErrorBoundary } from '@/components/error-boundary'
import { PageBreadcrumbs, PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { labStatusVariants } from '@/features/labs/lab-status'
import { formatLabDocumentFileSize } from '@/features/labs/lab-document-format'
import { getUploadedLabReviewReason, getUploadedLabSelectedReasonLabel } from '@/features/labs/uploaded-lab-analysis'
import {
  getJourneyDefinitionLabel,
  getQuestionnaireTemplateLabel,
  getWorkflowTemplateLabel,
  resolveJourneyTemplates,
} from '@/features/orders/order-intake-path'
import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query-keys'
import { formatDateTime } from '@/lib/utils'
import type { LabDetail } from '@optimist/schemas'

/** Lab detail page for integration-backed lab status and request context. */
export const Route = createFileRoute('/dashboard/labs/$labId')({
  component: LabReviewDetailPage,
})

async function openUploadedLabDocument(documentId: string, previewWindow: Window): Promise<void> {
  const blob = await api.getBlob(`/lab-documents/${documentId}/content`)
  const url = URL.createObjectURL(blob)
  previewWindow.location.replace(url)
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

function LabReviewDetailPage() {
  const { labId } = Route.useParams()
  const [uploadedDocumentError, setUploadedDocumentError] = useState<string | null>(null)
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.labs.detail(labId),
    queryFn: () => api.get<{ data: LabDetail }>(`/labs/${labId}`),
  })
  const lab = data?.data
  const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred'
  const hasPatientIdentity = Boolean(lab?.patientId || lab?.patientName)
  let resolvedOrderJourney: ReturnType<typeof resolveJourneyTemplates> | null = null
  let orderJourneyError: string | null = null
  if (lab !== undefined) {
    try {
      resolvedOrderJourney = resolveJourneyTemplates({
        journeyDefinitionId: lab.order.journeyDefinitionId,
        journeyDefinitionVersion: lab.order.journeyDefinitionVersion,
      })
    } catch (journeyError) {
      orderJourneyError =
        journeyError instanceof Error ? journeyError.message : 'The order journey reference is invalid.'
    }
  }

  async function handleOpenUploadedLabDocument(documentId: string): Promise<void> {
    setUploadedDocumentError(null)
    const previewWindow = window.open('about:blank', '_blank')
    if (previewWindow === null) {
      setUploadedDocumentError('Allow pop-ups for this site to view the uploaded PDF.')
      return
    }
    previewWindow.opener = null
    try {
      await openUploadedLabDocument(documentId, previewWindow)
    } catch {
      previewWindow.close()
      setUploadedDocumentError('The uploaded PDF could not be opened. Please try again.')
    }
  }

  return (
    <ErrorBoundary
      title="Lab detail page error"
      fallback={<ErrorBanner title="Lab unavailable" message="Something went wrong loading this page." />}
    >
      <div className="space-y-6">
        <PageHeader back={<PageBackLink to="/dashboard/labs" search={{ page: 1, limit: 50 }} label="Back to labs" />}
          breadcrumbs={
            <PageBreadcrumbs
              items={[
                <Link key="dashboard" to="/dashboard">Dashboard</Link>,
                <Link key="labs" to="/dashboard/labs" search={{ page: 1, limit: 50 }}>Labs</Link>,
                <span key="lab">{lab ? `Lab ${lab.id}` : `Lab ${labId}`}</span>,
              ]}
            />
          }
          title={lab ? `Lab ${lab.id}` : `Lab ${labId}`}
          actions={lab ? <StatusBadge variant={labStatusVariants[lab.status]}>{formatStatusValue(lab.status)}</StatusBadge> : null}
        />

        {error ? (
          <ErrorBanner title="Error loading lab" message={errorMessage} onRetry={() => void refetch()} />
        ) : null}

        {isLoading ? <LabDetailSkeleton /> : !lab ? (error ? null : <p className="text-body text-muted-foreground">Lab not found.</p>) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <div className="min-w-0 space-y-6 xl:col-span-2">
              <section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-xl bg-brand-muted text-brand">
                      <FileText size={18} strokeWidth={1.8} aria-hidden="true" />
                    </div>
                    <h2 className="font-inter text-section-title font-semibold text-foreground">Lab details</h2>
                  </div>
                </div>
                <dl className="grid gap-x-8 gap-y-5 p-6 sm:grid-cols-2">
                  {hasPatientIdentity ? <DetailItem label="Patient" value={lab.patientName ?? '-'} /> : null}
                  {lab.patientId ? <DetailItem label="Patient ID" value={lab.patientId} /> : null}
                  <DetailItem label="Order ID" value={lab.orderId} />
                  <DetailItem label="Vendor" value={lab.vendor ?? '-'} />
                  <DetailItem label="Integration order" value={lab.integration.externalLabOrderId ?? '-'} />
                  {lab.integration.externalPatientId ? (
                    <DetailItem label="External patient" value={lab.integration.externalPatientId} />
                  ) : null}
                  <DetailItem label="Ordered" value={formatMaybeDate(lab.orderedAt)} />
                  <DetailItem label="Last event" value={formatMaybeDate(lab.lastEventAt)} />
                </dl>
                {!hasPatientIdentity ? (
                  <p className="font-inter text-meta font-normal px-6 pb-6 text-muted-foreground">
                    Patient identity is hidden for your current role.
                  </p>
                ) : null}
              </section>

              <section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-xl bg-brand-muted text-brand">
                      <Clock size={18} strokeWidth={1.8} aria-hidden="true" />
                    </div>
                    <h2 className="font-inter text-section-title font-semibold text-foreground">Timeline</h2>
                  </div>
                </div>
                <div className="p-6">
                  {lab.timeline.length === 0 ? (
                    <p className="font-inter text-body font-normal text-muted-foreground">No lab timeline recorded yet.</p>
                  ) : (
                    <ol className="divide-y divide-border-subtle">
                      {lab.timeline.map((event) => (
                        <li key={`${event.status}-${event.recordedAt}`} className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3 first:pt-0 last:pb-0">
                          <StatusBadge variant={labStatusVariants[event.status]}>{formatStatusValue(event.status)}</StatusBadge>
                          <time dateTime={event.recordedAt} className="font-inter text-meta tabular-nums text-muted-foreground">{formatMaybeDate(event.recordedAt)}</time>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </section>

              {lab.uploadedDocuments && lab.uploadedDocuments.length > 0 ? (
                <section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                  <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
                    <div className="flex items-center gap-3">
                      <div className="flex size-9 items-center justify-center rounded-xl bg-brand-muted text-brand">
                        <FileText size={18} strokeWidth={1.8} aria-hidden="true" />
                      </div>
                      <h2 className="font-inter text-section-title font-semibold text-foreground">Uploaded Lab PDFs</h2>
                    </div>
                  </div>
                  <div className="space-y-3 p-6">
                    {lab.uploadedDocuments.map((document) => (
                      <div key={document.id} className="overflow-hidden rounded-xl border border-border-subtle">
                        <div className="flex flex-wrap items-center justify-between gap-4 bg-muted p-4">
                          <div className="flex min-w-0 flex-1 items-center gap-4">
                            <div aria-hidden="true" className="flex h-14 w-11 shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-border bg-card text-muted-foreground">
                              <FileText size={20} strokeWidth={1.5} />
                              <span className="font-inter text-label font-semibold">PDF</span>
                            </div>
                            <div className="min-w-0 space-y-1">
                              <p className="break-words font-inter text-body font-semibold text-foreground">{document.originalFileName}</p>
                              <p className="font-inter text-meta tabular-nums text-muted-foreground">{formatLabDocumentFileSize(document.sizeBytes)}</p>
                              <p className="font-inter text-meta text-muted-foreground">{formatStatusValue(document.status)}</p>
                            </div>
                          </div>
                          <Button variant="secondary" type="button" onClick={() => void handleOpenUploadedLabDocument(document.id)}>
                            <ExternalLink size={16} aria-hidden="true" />
                            View PDF
                          </Button>
                        </div>
                        {document.genHealthDocument ? (
                          <dl className="grid gap-x-8 gap-y-4 border-t border-border-subtle p-4 sm:grid-cols-2">
                            <DetailItem label="GenHealth document" value={document.genHealthDocument.externalDocumentId} />
                            <DetailItem label="Document type" value={document.genHealthDocument.documentType} />
                          </dl>
                        ) : null}
                        {document.aiAnalysis ? (
                          <dl className="grid gap-x-8 gap-y-4 border-t border-border-subtle p-4 sm:grid-cols-2">
                            <DetailItem label="Automation status" value={formatStatusValue(document.aiAnalysis.status)} />
                            <DetailItem
                              label="Eligibility"
                              value={document.aiAnalysis.eligibilityResult ? formatStatusValue(document.aiAnalysis.eligibilityResult) : '-'}
                            />
                            <DetailItem label="Selected date" value={document.aiAnalysis.selectedValidityDate ?? '-'} />
                            <DetailItem label="Cutoff date" value={document.aiAnalysis.eligibilityCutoffDate ?? '-'} />
                            <DetailItem
                              label="Review reason"
                              value={getUploadedLabReviewReason(document.aiAnalysis)}
                            />
                            {(() => {
                              const selectedReasonLabel = getUploadedLabSelectedReasonLabel(document.aiAnalysis)
                              if (selectedReasonLabel === null) {
                                return null
                              }

                              return <DetailItem label="Date selection reason" value={selectedReasonLabel} />
                            })()}
                            <DetailItem
                              label="Support review"
                              value={
                                document.aiAnalysis.supportReviewRequestedAt
                                  ? formatMaybeDate(document.aiAnalysis.supportReviewRequestedAt)
                                  : '-'
                              }
                            />
                          </dl>
                        ) : null}
                      </div>
                    ))}
                    {uploadedDocumentError === null ? null : (
                      <p role="alert" className="font-inter text-meta text-destructive">
                        {uploadedDocumentError}
                      </p>
                    )}
                  </div>
                </section>
              ) : null}
            </div>

            <div className="min-w-0 space-y-6">
              <section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-muted text-brand">
                      <Activity size={18} strokeWidth={1.8} aria-hidden="true" />
                    </div>
                    <h2 className="font-inter text-section-title font-semibold text-foreground">Integration</h2>
                  </div>
                </div>
                <dl className="grid gap-x-6 gap-y-4 p-6 sm:grid-cols-2 xl:grid-cols-1">
                  <DetailItem label="Adapter" value={lab.integration.adapterKey ? formatStatusValue(lab.integration.adapterKey) : '-'} />
                  <DetailItem label="Case status" value={lab.integration.caseStatus ? formatStatusValue(lab.integration.caseStatus) : '-'} />
                  <DetailItem label="Last request" value={formatMaybeDate(lab.integration.lastRequestAt)} />
                  <DetailItem label="Last success" value={formatMaybeDate(lab.integration.lastSuccessAt)} />
                  <DetailItem label="Last failure" value={formatMaybeDate(lab.integration.lastFailureAt)} />
                  <DetailItem label="HTTP status" value={lab.integration.lastHttpStatus?.toString() ?? '-'} />
                  <DetailItem label="Error code" value={lab.integration.lastErrorCode ?? '-'} />
                  {lab.integration.lastErrorMessage === undefined ? null : (
                    <DetailItem label="Error message" value={lab.integration.lastErrorMessage ?? '-'} />
                  )}
                </dl>
              </section>

              <section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-muted text-brand">
                      <Clock size={18} strokeWidth={1.8} aria-hidden="true" />
                    </div>
                    <h2 className="font-inter text-section-title font-semibold text-foreground">Latest request</h2>
                  </div>
                </div>
                <div className="p-6">
                {lab.latestRequest ? (
                  <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-1">
                    <DetailItem label="Status" value={lab.latestRequest.status} />
                    <DetailItem label="Attempt" value={String(lab.latestRequest.attemptNumber)} />
                    <DetailItem label="Started" value={formatMaybeDate(lab.latestRequest.startedAt)} />
                    <DetailItem label="Completed" value={formatMaybeDate(lab.latestRequest.completedAt)} />
                    <DetailItem label="External order" value={lab.latestRequest.externalOrderId ?? '-'} />
                  </dl>
                ) : (
                  <p className="font-inter text-body font-normal text-muted-foreground">No integration request recorded yet.</p>
                )}
                </div>
              </section>

              <section className="rounded-2xl border border-border bg-card shadow-card overflow-hidden">
                <div className="flex items-start justify-between gap-4 border-b border-border-subtle px-6 py-5">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-muted text-brand">
                      <ShoppingBag size={18} strokeWidth={1.8} aria-hidden="true" />
                    </div>
                    <h2 className="font-inter text-section-title font-semibold text-foreground">Related order</h2>
                  </div>
                </div>
                <dl className="grid gap-x-6 gap-y-4 p-6 sm:grid-cols-2 xl:grid-cols-1">
                  <DetailItem label="Order ID" value={lab.order.id} />
                  <DetailItem label="Current status" value={formatStatusValue(lab.order.displayStatus)} />
                  {resolvedOrderJourney === null ? null : (
                    <>
                      <DetailItem
                        label="Journey definition"
                        value={getJourneyDefinitionLabel(resolvedOrderJourney.definition)}
                      />
                      <DetailItem
                        label="Workflow template"
                        value={getWorkflowTemplateLabel(resolvedOrderJourney.workflowTemplate)}
                      />
                      <DetailItem
                        label="Questionnaire template"
                        value={getQuestionnaireTemplateLabel(resolvedOrderJourney.questionnaireTemplate)}
                      />
                    </>
                  )}
                </dl>
                <div className="px-6 pb-6">
                  <Button asChild variant="secondary" className="w-full">
                    <Link to="/dashboard/orders/$orderId" params={{ orderId: lab.order.id }}>View order</Link>
                  </Button>
                </div>
                {resolvedOrderJourney === null ? (
                  <p className="mx-6 mb-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 font-inter text-body font-normal text-red-700">
                    {orderJourneyError === null
                      ? 'The order journey could not be resolved.'
                      : orderJourneyError}
                  </p>
                ) : null}
              </section>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  )
}

function DetailItem(props: { label: string; value: string }) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="font-inter text-meta text-muted-foreground">{props.label}</dt>
      <dd className="break-words font-inter text-data font-medium tabular-nums text-foreground-secondary">
        {props.value}
      </dd>
    </div>
  )
}

function formatStatusValue(value: string): string {
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (character) => character.toUpperCase())
}

function formatMaybeDate(value: string | null | undefined): string {
  return value ? formatDateTime(value) : '-'
}
